import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { Pressable } from 'native-base';
import React from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { RiskRating } from '../../../../../../../../../components/RiskRating';
import { TableContent } from '../../../../../../../../../components/TableContent';
import { useGlobalAssetContext } from '../../../../../contexts';
import { useCoesAssetContext } from '../../../../../contexts/products/Coes';
import { useCoesAssetController } from '../../../../../hooks/products/Coes';
import { AssetTitleWithSubtitle } from '../../../components/AssetTitleWithSubtitle';
import { AssetValueWithVariation } from '../../../components/AssetValueWithVariation';
import { LateralBar } from '../../../components/LateralBar';
import { ListLoadingItemMobile } from '../ListLoadingItem/mobile';

import { CoesListItemTypes } from '.';

export const CoesListItemMobile: React.FC<CoesListItemTypes.Props> = ({ offer }) => {
  const id = offer.productId;
  const {
    control,
    currentValue,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    isFocused,
    currentValueFormatted,
  } = useCoesAssetController(offer);
  const { setModalOpen, setOfferSelected } = useCoesAssetContext();
  const { infoGetter, selectedTab } = useGlobalAssetContext();

  const completeOffer = { ...offer, orderValue: currentValue };

  const { Button, isSelected, forceLoading } = infoGetter('coes', completeOffer, 'productId');

  const onOpenDetailsModal = () => {
    setModalOpen(true);
    setOfferSelected(offer);
  };

  const middleInfos1 = [
    {
      label: 'Risco',
      value: (
        <RiskRating
          currentRiskRating={offer.riskRatingScoreNormalized}
          tooltip={offer.riskRatingNormalized}
        />
      ),
    },
    {
      label: 'Vencimento',
      value: <UI.Text color="neutral.darkest">{offer.maturityDateNormalized}</UI.Text>,
    },
  ];

  const middleInfos2 = [
    ...offer.additionalInfoNormalized.map((info) => ({
      label: info.labelNormalized,
      value: <AssetValueWithVariation value={info.valueNormalized} variation={info.variation} />,
    })),
  ];

  if (forceLoading) {
    return <ListLoadingItemMobile />;
  }

  return (
    <TableContent
      direction="row"
      space={0}
      p={0}
      px={Spacing['inline.xxxs']}
      isHighlighted={isSelected}>
      <LateralBar
        backgroundColor={selectedTab.color}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack padding={Spacing['inset.sm']} space={Spacing['stack.xxxs']} w="full">
        <HStack justifyContent="space-between" alignItems="center">
          <AssetTitleWithSubtitle
            title={offer.productNameNormalized}
            subtitle={offer.strategyNormalized}
          />
          <Pressable onPress={onOpenDetailsModal}>
            <UI.Icon name="Info" size="xs" />
          </Pressable>
        </HStack>
        <HStack justifyContent="space-between">
          {middleInfos1.map(({ label, value }) => (
            <VStack space={Spacing['stack.quarck']} key={label}>
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <HStack justifyContent="space-between">
          {middleInfos2.map(({ label, value }) => (
            <VStack space={Spacing['stack.quarck']} key={label}>
              <UI.Text color="neutral.black" variant="semibold">
                {label}
              </UI.Text>
              {value}
            </VStack>
          ))}
        </HStack>
        <HStack space={Spacing['inline.xxxs']} justifyContent="space-between" w="full">
          <VStack space={Spacing['stack.quarck']} w="75%">
            <UI.Text color="neutral.black" variant="semibold">
              Valor a aplicar:
            </UI.Text>
            <InputMoney
              id={`coes-input${id}`}
              name="createAppliedAmount"
              control={control}
              handleFocus={handleInputFocus}
              handleBlur={handleInputBlur}
              handleSubmit={handleSubmit}
              handleClear={handleInputClear}
              isDisabled={isSelected}
              currentValue={currentValueFormatted}
              hasFocus={isFocused}
              minValue={offer.minimumApplication}
              customInputProps={{
                ...{ ...(isFocused && hasError && { error: hasError }) },
                ...{ ...(isFocused && !hasError && { helpText }) },
              }}
            />
          </VStack>
          <Stack alignSelf="flex-end">
            <Button
              id={selectedTab.typeGetterId}
              offer={completeOffer}
              isDisabled={typeof hasError?.message === 'string'}
              productContextConstant={selectedTab}
            />
          </Stack>
        </HStack>
      </VStack>
    </TableContent>
  );
};
